import { useApolloClient } from "@apollo/client";
import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import { FETCH_INSTANCES_LIST as fetchInstancesList } from "../../../graphql/query/Instance";
import {
  buildInstanceNavigationSnapshot,
  getInstanceNavigationState,
} from "./instanceNavigationContext";
import { instanceNavigationStore } from "./useInstanceFilters";

const NAVIGATION_SNAPSHOT_MAX_AGE_MS = 5 * 60 * 1000;
const NAVIGATION_REDUCER_ACTIONS = {
  HYDRATION_FINISHED: "HYDRATION_FINISHED",
  HYDRATION_STARTED: "HYDRATION_STARTED",
  NAVIGATION_FETCH_FINISHED: "NAVIGATION_FETCH_FINISHED",
  NAVIGATION_FETCH_STARTED: "NAVIGATION_FETCH_STARTED",
  SNAPSHOT_UPDATED: "SNAPSHOT_UPDATED",
};

const buildInitialNavigationState = navigationSnapshot => ({
  isPrefillingNavigation: false,
  navigationLoading: false,
  navigationSnapshot,
});

const instanceNavigationReducer = (state, action) => {
  switch (action.type) {
    case NAVIGATION_REDUCER_ACTIONS.NAVIGATION_FETCH_STARTED:
      return {
        ...state,
        navigationLoading: true,
      };
    case NAVIGATION_REDUCER_ACTIONS.NAVIGATION_FETCH_FINISHED:
      return {
        ...state,
        navigationLoading: false,
      };
    case NAVIGATION_REDUCER_ACTIONS.HYDRATION_STARTED:
      return {
        ...state,
        isPrefillingNavigation: true,
      };
    case NAVIGATION_REDUCER_ACTIONS.HYDRATION_FINISHED:
      return {
        ...state,
        isPrefillingNavigation: false,
      };
    case NAVIGATION_REDUCER_ACTIONS.SNAPSHOT_UPDATED:
      return {
        ...state,
        navigationSnapshot: action.payload,
      };
    default:
      return state;
  }
};

const isTypingTarget = target => {
  if (!(target instanceof HTMLElement)) return false;

  const tagName = target?.tagName?.toLowerCase();
  return (
    target.isContentEditable ||
    ["input", "textarea", "select"].includes(tagName) ||
    target.closest("[contenteditable='true']")
  );
};

const useInstanceNavigation = ({
  activeSubPage = "details",
  instanceIdNumber,
  listContext,
  listContextQuery,
  navigate,
  status,
}) => {
  const apolloClient = useApolloClient();
  const exhaustedHydrationKeysRef = useRef(new Set());
  const [{ isPrefillingNavigation, navigationLoading, navigationSnapshot }, dispatch] =
    useReducer(
      instanceNavigationReducer,
      instanceNavigationStore.get(),
      buildInitialNavigationState,
    );

  useEffect(() => {
    const unsubscribe = instanceNavigationStore.subscribe(nextSnapshot => {
      dispatch({
        payload: nextSnapshot,
        type: NAVIGATION_REDUCER_ACTIONS.SNAPSHOT_UPDATED,
      });
    });
    return () => unsubscribe();
  }, []);

  const buildListContextQueryFromSnapshot = useCallback(snapshotListContext => {
    return [
      snapshotListContext?.listId ? `listId=${snapshotListContext.listId}` : null,
      snapshotListContext?.imageListId
        ? `imageListId=${snapshotListContext.imageListId}`
        : null,
      snapshotListContext?.workingListId
        ? `workingListId=${snapshotListContext.workingListId}`
        : null,
      snapshotListContext?.imageWorkingListId
        ? `imageWorkingListId=${snapshotListContext.imageWorkingListId}`
        : null,
    ]
      .filter(Boolean)
      .join("&");
  }, []);

  const getTargetDetailsUrl = useCallback(
    targetId => {
      const snapshotListContextQuery = buildListContextQueryFromSnapshot(
        navigationSnapshot?.listContext || {},
      );
      const queryContext = snapshotListContextQuery || listContextQuery;
      const targetStatus = navigationSnapshot?.statusContext || "all";
      return `/instance/${targetId}/${activeSubPage}?status=${targetStatus}${queryContext ? `&${queryContext}` : ""}`;
    },
    [
      activeSubPage,
      buildListContextQueryFromSnapshot,
      listContextQuery,
      navigationSnapshot,
    ],
  );

  const navigationState = useMemo(
    () =>
      getInstanceNavigationState({
        instanceId: instanceIdNumber,
        snapshot: navigationSnapshot,
      }),
    [instanceIdNumber, navigationSnapshot],
  );
  const indexOnCurrentPage = useMemo(() => {
    if (!instanceIdNumber) return -1;
    return (navigationSnapshot?.pageIds || []).findIndex(
      id => id === instanceIdNumber,
    );
  }, [instanceIdNumber, navigationSnapshot]);

  const currentInstanceInSnapshot = useMemo(() => {
    if (!instanceIdNumber) return false;
    return (navigationSnapshot?.pageIds || []).includes(instanceIdNumber);
  }, [instanceIdNumber, navigationSnapshot]);
  const hasNavigationContext = useMemo(
    () => (navigationSnapshot?.pageIds || []).length > 0,
    [navigationSnapshot],
  );

  const shouldForceAllContext = status === "all";
  const isSnapshotStale =
    Date.now() - Number(navigationSnapshot?.updatedAt || 0) >
    NAVIGATION_SNAPSHOT_MAX_AGE_MS;
  const snapshotStatusMatches = shouldForceAllContext
    ? navigationSnapshot?.statusContext === "all"
    : true;
  const canUseExistingSnapshot =
    snapshotStatusMatches && currentInstanceInSnapshot && !isSnapshotStale;
  const hydrationContextKey = useMemo(
    () =>
      JSON.stringify({
        instanceIdNumber,
        listContext,
        shouldForceAllContext,
        status,
      }),
    [instanceIdNumber, listContext, shouldForceAllContext, status],
  );
  const isHydrationExhaustedForContext = exhaustedHydrationKeysRef.current.has(
    hydrationContextKey,
  );

  useEffect(() => {
    if (!canUseExistingSnapshot && !isSnapshotStale) return;
    exhaustedHydrationKeysRef.current.delete(hydrationContextKey);
  }, [canUseExistingSnapshot, hydrationContextKey, isSnapshotStale]);

  const fetchNavigationPage = useCallback(
    async ({ pageOffset, persistSnapshot = true, snapshotOverride = null }) => {
      const currentSnapshot = snapshotOverride || instanceNavigationStore.get();
      const queryInput = {
        ...(currentSnapshot?.currentSearchParams || {}),
        limit:
          currentSnapshot?.limit ||
          currentSnapshot?.currentSearchParams?.limit ||
          50,
        offset: pageOffset,
      };

      dispatch({
        type: NAVIGATION_REDUCER_ACTIONS.NAVIGATION_FETCH_STARTED,
      });
      try {
        const { data } = await apolloClient.query({
          query: fetchInstancesList,
          variables: { input: queryInput },
          fetchPolicy: "network-only",
        });
        const { instancesV2 } = data || {};
        const nextSnapshot = buildInstanceNavigationSnapshot({
          count: instancesV2?.count || 0,
          edges: instancesV2?.edges || [],
          listContext: currentSnapshot?.listContext || {},
          queryVariables: queryInput,
          statusContext: currentSnapshot?.statusContext || "all",
        });
        if (persistSnapshot) {
          instanceNavigationStore.set(nextSnapshot);
        }
        return nextSnapshot;
      } finally {
        dispatch({
          type: NAVIGATION_REDUCER_ACTIONS.NAVIGATION_FETCH_FINISHED,
        });
      }
    },
    [apolloClient],
  );

  const navigateToPreviousInstance = useCallback(async () => {
    if (!instanceIdNumber || navigationLoading || !navigationState?.hasPrevious) {
      return;
    }

    if (navigationState?.previousId) {
      navigate(getTargetDetailsUrl(navigationState.previousId));
      return;
    }

    if (navigationState?.previousOffset === null) return;

    const nextSnapshot = await fetchNavigationPage({
      pageOffset: navigationState.previousOffset,
    });
    const previousPageIds = nextSnapshot?.pageIds || [];
    const previousPageLastId = previousPageIds[previousPageIds.length - 1];
    if (previousPageLastId) {
      navigate(getTargetDetailsUrl(previousPageLastId));
    }
  }, [
    fetchNavigationPage,
    getTargetDetailsUrl,
    instanceIdNumber,
    navigate,
    navigationLoading,
    navigationState,
  ]);

  const navigateToNextInstance = useCallback(async () => {
    if (!instanceIdNumber || navigationLoading || !navigationState?.hasNext) {
      return;
    }

    if (navigationState?.nextId) {
      navigate(getTargetDetailsUrl(navigationState.nextId));
      return;
    }

    if (navigationState?.nextOffset === null) return;

    const nextSnapshot = await fetchNavigationPage({
      pageOffset: navigationState.nextOffset,
    });
    const nextPageFirstId = nextSnapshot?.pageIds?.[0];
    if (nextPageFirstId) {
      navigate(getTargetDetailsUrl(nextPageFirstId));
    }
  }, [
    fetchNavigationPage,
    getTargetDetailsUrl,
    instanceIdNumber,
    navigate,
    navigationLoading,
    navigationState,
  ]);

  useEffect(() => {
    if (
      !instanceIdNumber ||
      !hasNavigationContext ||
      canUseExistingSnapshot ||
      isPrefillingNavigation ||
      isHydrationExhaustedForContext
    ) {
      return;
    }

    let isCancelled = false;
    let isCurrentHydration = true;
    const hydrateNavigationContext = async () => {
      dispatch({
        type: NAVIGATION_REDUCER_ACTIONS.HYDRATION_STARTED,
      });
      try {
        const baseSnapshot = instanceNavigationStore.get();
        const initialSnapshot = {
          ...baseSnapshot,
          // On direct `status=all` detail routes, ignore stale search filters.
          currentSearchParams: shouldForceAllContext
            ? {}
            : baseSnapshot?.currentSearchParams || {},
          limit: baseSnapshot?.limit || 50,
          listContext:
            Object.keys(baseSnapshot?.listContext || {}).length > 0
              ? baseSnapshot.listContext
              : listContext,
          statusContext: shouldForceAllContext
            ? "all"
            : baseSnapshot?.statusContext || "all",
        };

        const fetchedSnapshot = await fetchNavigationPage({
          pageOffset: initialSnapshot?.offset || 0,
          persistSnapshot: false,
          snapshotOverride: initialSnapshot,
        });

        if (!isCancelled && (fetchedSnapshot?.pageIds || []).includes(instanceIdNumber)) {
          exhaustedHydrationKeysRef.current.delete(hydrationContextKey);
          instanceNavigationStore.set(fetchedSnapshot);
          return;
        }

        if (!isCancelled && fetchedSnapshot) {
          instanceNavigationStore.set(fetchedSnapshot);
          exhaustedHydrationKeysRef.current.add(hydrationContextKey);
        }
      } finally {
        if (isCurrentHydration) {
          dispatch({
            type: NAVIGATION_REDUCER_ACTIONS.HYDRATION_FINISHED,
          });
        }
      }
    };

    hydrateNavigationContext();
    return () => {
      isCancelled = true;
      isCurrentHydration = false;
      // Ensure UI can recover if an in-flight hydration is cancelled/restarted.
      dispatch({
        type: NAVIGATION_REDUCER_ACTIONS.HYDRATION_FINISHED,
      });
    };
  }, [
    canUseExistingSnapshot,
    fetchNavigationPage,
    hasNavigationContext,
    hydrationContextKey,
    instanceIdNumber,
    isHydrationExhaustedForContext,
    isPrefillingNavigation,
    listContext,
    shouldForceAllContext,
  ]);

  const showInstanceNavigationControls =
    typeof instanceIdNumber === "number" && Number.isFinite(instanceIdNumber);
  const hasPrevious = hasNavigationContext && Boolean(navigationState?.hasPrevious);
  const hasNext = hasNavigationContext && Boolean(navigationState?.hasNext);
  const isNavigationBusy = navigationLoading || isPrefillingNavigation;

  useEffect(() => {
    if (!showInstanceNavigationControls) return;

    const onKeyDown = event => {
      if (event.defaultPrevented || event.repeat) return;
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
        return;
      }
      if (isTypingTarget(event.target)) return;

      if (event.key === "ArrowLeft" && navigationState?.hasPrevious) {
        event.preventDefault();
        navigateToPreviousInstance();
      }

      if (event.key === "ArrowRight" && navigationState?.hasNext) {
        event.preventDefault();
        navigateToNextInstance();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    navigationState?.hasNext,
    navigationState?.hasPrevious,
    navigateToNextInstance,
    navigateToPreviousInstance,
    showInstanceNavigationControls,
  ]);

  return {
    hasNavigationContext,
    hasNext,
    hasPrevious,
    navigationLoading: isNavigationBusy,
    navigationSnapshot,
    navigateToNextInstance,
    navigateToPreviousInstance,
    showInstanceNavigationControls,
  };
};

export {
  buildInitialNavigationState,
  instanceNavigationReducer,
  NAVIGATION_REDUCER_ACTIONS,
};

export default useInstanceNavigation;
